// Gemini APIとの連携を行うサービス
// 実際の実装ではGemini APIを使用する

export type RewriteGenre = 'general' | 'blog' | 'twitter' | 'meeting' | 'idea' | 'summary';

export interface CustomPrompt {
  id: string;
  name: string;
  genre: RewriteGenre | 'custom';
  prompt: string;
  isDefault?: boolean;
}

export interface WritingStyle {
  id: string;
  name: string;
  instruction: string;
  isDefault?: boolean;
}

export interface GeminiRewriteRequest {
  text: string;
  comment?: string;
  genre?: RewriteGenre;
  customPrompt?: string;
  writingStyle?: string;
}

export interface GeminiRewriteResponse {
  rewrittenText: string;
  tokenUsage?: {
    input: number;
    output: number;
    total: number;
  };
  error?: string;
}

export class GeminiApiClient {
  private apiKey: string;
  private baseUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

  constructor(apiKey?: string) {
    this.apiKey = apiKey || 'AIzaSyCwYScZQ_Q8nrcWkKJ-_9kKIbfQu5YP6RU';
  }

  // API使用量を取得（モック実装 - 実際のAPIでは使用量エンドポイントが必要）
  async getUsage(): Promise<{ used: number; limit: number; remaining: number }> {
    return {
      used: 1250,
      limit: 10000,
      remaining: 8750
    };
  }

  // テキストをリライト
  async rewrite(request: GeminiRewriteRequest): Promise<GeminiRewriteResponse> {
    const { text, comment, genre = 'general', customPrompt, writingStyle } = request;
    
    // 入力が空の場合はエラー
    if (!text.trim()) {
      return {
        rewrittenText: '',
        error: '入力テキストが空です'
      };
    }

    try {
      // 最大3回まで継続リクエストを試行
      let fullResponse = '';
      let attemptCount = 0;
      const maxAttempts = 3;
      
      while (attemptCount < maxAttempts) {
        attemptCount++;
        console.log(`API呼び出し試行 ${attemptCount}/${maxAttempts}`);
        
        // プロンプトを作成（継続の場合は調整）
        let currentPrompt;
        if (attemptCount === 1) {
          currentPrompt = customPrompt || this.buildPrompt(text, genre, comment, writingStyle);
        } else {
          // 継続リクエストの場合
          currentPrompt = this.buildContinuationPrompt(text, fullResponse, genre, comment, writingStyle);
        }
        
        const apiResponse = await this.callGeminiAPI(currentPrompt);
        
        if (apiResponse.error) {
          throw new Error(apiResponse.error);
        }
        
        const currentResponse = apiResponse.text;
        const finishReason = apiResponse.finishReason;
        
        console.log(`試行${attemptCount}: finishReason=${finishReason}, length=${currentResponse.length}`);
        
        // 応答を連結
        if (attemptCount === 1) {
          fullResponse = currentResponse;
        } else {
          // 重複部分を削除して連結
          fullResponse = this.mergeResponses(fullResponse, currentResponse);
        }
        
        // finishReasonに基づく継続判定
        const shouldContinue = this.shouldContinueBasedOnFinishReason(finishReason, fullResponse, text.length, attemptCount);
        
        if (!shouldContinue.continue) {
          console.log(`継続停止: ${shouldContinue.reason}`);
          break;
        }
        
        // 文字数が増えていない場合は継続を停止
        if (attemptCount > 1 && currentResponse.trim().length < 50) {
          console.log('応答の増加が少なすぎるため継続を停止');
          break;
        }
      }
      
      return {
        rewrittenText: fullResponse,
        tokenUsage: {
          input: text.length,
          output: fullResponse.length,
          total: text.length + fullResponse.length
        }
      };
    } catch (error) {
      console.error('Gemini API Error:', error);
      
      // APIエラー時はフォールバックとしてモック応答を返す
      return this.getMockResponse(text, genre, comment, customPrompt, writingStyle);
    }
  }

  // Gemini APIを呼び出すヘルパーメソッド
  private async callGeminiAPI(prompt: string): Promise<{text: string, finishReason?: string, error?: string}> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒でタイムアウト
      
      const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.9,
            topK: 64,
            topP: 0.98,
            maxOutputTokens: 16384,
            candidateCount: 1,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_ONLY_HIGH"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH", 
              threshold: "BLOCK_ONLY_HIGH"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_ONLY_HIGH"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_ONLY_HIGH"
            }
          ]
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return { text: '', error: `API Error: ${response.status} ${response.statusText}` };
      }

      const data = await response.json();
      
      if (!data.candidates || data.candidates.length === 0) {
        return { text: '', error: 'APIからの応答が空です' };
      }

      // 詳細なデバッグ情報
      console.log('=== Gemini API Complete Response ===');
      console.log('Full API Response:', JSON.stringify(data, null, 2));
      console.log('Candidates Count:', data.candidates?.length);
      console.log('Finish Reason:', data.candidates?.[0]?.finishReason);
      console.log('Safety Ratings:', data.candidates?.[0]?.safetyRatings);
      console.log('Usage Metadata:', data.usageMetadata);
      console.log('Prompt Feedback:', data.promptFeedback);
      console.log('Response Length:', data.candidates?.[0]?.content?.parts?.[0]?.text?.length);
      console.log('Response End (last 100 chars):', data.candidates?.[0]?.content?.parts?.[0]?.text?.slice(-100));
      console.log('=======================================');
      
      const responseText = data.candidates[0].content.parts[0].text;
      const finishReason = data.candidates[0].finishReason;
      
      return { text: responseText, finishReason };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return { text: '', error: 'リクエストがタイムアウトしました' };
      }
      return { text: '', error: error instanceof Error ? error.message : 'unknown error' };
    }
  }

  // finishReasonに基づく継続判定
  private shouldContinueBasedOnFinishReason(finishReason: string | undefined, fullResponse: string, inputLength: number, attemptCount: number): {continue: boolean, reason: string} {
    // finishReasonの分析
    console.log(`継続判定: finishReason=${finishReason}, responseLength=${fullResponse.length}, attempt=${attemptCount}`);
    
    // STOPは正常終了だが、レスポンスが短い場合は継続を検討
    if (finishReason === 'STOP') {
      const isComplete = this.isResponseComplete(fullResponse, inputLength);
      if (isComplete) {
        return { continue: false, reason: 'STOP reason with complete response' };
      } else {
        return { continue: true, reason: 'STOP reason but response seems incomplete' };
      }
    }
    
    // LENGTHは最大トークン数に達した場合 - 継続すべき
    if (finishReason === 'MAX_TOKENS' || finishReason === 'LENGTH') {
      return { continue: true, reason: 'Hit token limit, continuing...' };
    }
    
    // SAFETYやRECITATION等の場合は継続しない
    if (finishReason === 'SAFETY' || finishReason === 'RECITATION') {
      return { continue: false, reason: `Safety/Content issue: ${finishReason}` };
    }
    
    // OTHER、FINISH_REASON_UNSPECIFIED等
    if (finishReason === 'OTHER' || finishReason === 'FINISH_REASON_UNSPECIFIED') {
      // レスポンスの長さで判定
      const isComplete = this.isResponseComplete(fullResponse, inputLength);
      return { 
        continue: !isComplete, 
        reason: isComplete ? 'Other finish reason with complete response' : 'Other finish reason, continuing...' 
      };
    }
    
    // 未知のfinishReasonまたはundefined
    console.warn(`Unknown finishReason: ${finishReason}`);
    const isComplete = this.isResponseComplete(fullResponse, inputLength);
    return { 
      continue: !isComplete && attemptCount < 3, 
      reason: `Unknown finish reason, continuing based on completeness: ${isComplete}` 
    };
  }

  // 応答が完結しているかチェック
  private isResponseComplete(response: string, inputLength: number): boolean {
    const trimmedResponse = response.trim();
    
    // 最小文字数チェック（入力の1.5倍以上または1000文字以上）
    const minLength = Math.max(inputLength * 1.5, 1000);
    if (trimmedResponse.length < minLength) {
      console.log(`応答が短すぎます: ${trimmedResponse.length} < ${minLength}`);
      return false;
    }
    
    // 文章の終わり方をチェック
    const completionPatterns = [
      /[。！？\.]$/,  // 句読点で終わっている
      /です$/,        // です調で終わっている
      /ます$/,        // ます調で終わっている
      /である$/,       // である調で終わっている
      /だ$/,          // だ調で終わっている
      /でしょう$/,     // でしょう調で終わっている
      /ません$/,       // ません調で終わっている
      /ください$/,     // ください調で終わっている
    ];
    
    const hasProperEnding = completionPatterns.some(pattern => 
      pattern.test(trimmedResponse)
    );
    
    if (!hasProperEnding) {
      console.log('応答が適切に終了していません:', trimmedResponse.slice(-50));
      return false;
    }
    
    return true;
  }

  // 継続プロンプトを作成
  private buildContinuationPrompt(originalText: string, previousResponse: string, _genre: RewriteGenre, comment?: string, writingStyle?: string): string {
    // 前回の応答の最後の部分を取得して、継続のコンテキストを提供
    const lastPart = previousResponse.trim().slice(-200);
    
    let prompt = `以下の文章の続きを書いてください。前回の応答が途中で終わっているので、その続きから自然に書き続けてください。

前回の応答の最後の部分:
"...${lastPart}"

この文章の続きを書いてください。前回の内容と重複しないよう注意して、自然な流れで続きを書いてください。

元の入力内容（参考）:
${originalText.slice(0, 500)}${originalText.length > 500 ? '...' : ''}`;

    if (comment) {
      prompt += `\n\n追加の指示:\n${comment}`;
    }

    if (writingStyle) {
      prompt += `\n\n【ライティングスタイル指示】\n${writingStyle}`;
    }

    prompt += `\n\n重要な指示：
1. 前回の応答の続きから自然に書き始めてください
2. 前回の内容を繰り返さないでください
3. 文章を途中で終わらせず、完結させてください
4. 最低でも1000文字以上書いてください
5. 内容を省略せず、詳細に展開してください`;

    return prompt;
  }

  // 応答をマージ（重複削除）
  private mergeResponses(previous: string, current: string): string {
    const previousTrimmed = previous.trim();
    const currentTrimmed = current.trim();
    
    // 前回の応答の最後の50文字と今回の応答の最初の50文字を比較
    const previousEnd = previousTrimmed.slice(-50);
    const currentStart = currentTrimmed.slice(0, 50);
    
    // 重複部分を探す
    for (let i = 10; i <= Math.min(previousEnd.length, currentStart.length); i++) {
      if (previousEnd.slice(-i) === currentStart.slice(0, i)) {
        console.log(`重複部分を検出: "${previousEnd.slice(-i)}"`);
        return previousTrimmed + ' ' + currentTrimmed.slice(i);
      }
    }
    
    // 重複が見つからない場合はスペースで連結
    return previousTrimmed + ' ' + currentTrimmed;
  }

  private buildPrompt(text: string, genre: RewriteGenre, comment?: string, writingStyle?: string): string {
    const genrePrompts: Record<RewriteGenre, string> = {
      general: `以下の入力内容を読みやすく整形し、適切な見出し、箇条書き、強調などを使ってマークダウン形式で整理してください。

重要：内容を省略せず、詳細に展開して書いてください。文章は最後まで完結させてください。
前置きや挨拶は一切不要です。整理された内容のみを出力してください。`,
      
      blog: `以下の入力内容を魅力的なブログ記事として構造化してください。
SEOを意識した見出し、読みやすい段落、適切な強調表現を使い、読者を引きつける内容にしてください。

重要：内容を省略せず、詳細に展開して書いてください。文章は最後まで完結させてください。
前置きや挨拶は一切不要です。ブログ記事の本文のみを出力してください。`,
      
      twitter: `以下の入力内容を140文字以内のX（Twitter）投稿に変換してください。

重要：
- 「投稿案を作成します」などの前置きは一切不要
- 投稿本文のみを出力
- 140文字以内厳守
- 適切なハッシュタグを含める`,
      
      meeting: `以下の入力内容を議事録形式にまとめてください。

前置きや挨拶は一切不要です。議事録の内容のみを出力してください。
- 日時・場所・参加者
- 議題と討議内容  
- 決定事項
- アクションアイテム`,
      
      idea: `以下の入力内容をアイデア企画書として構造化してください。

前置きや挨拶は一切不要です。企画書の内容のみを出力してください。
- アイデアの核心
- 背景・課題
- 具体的な内容
- 実現可能性
- 次のステップ`,
      
      summary: `以下の入力内容を簡潔に要約してください。

前置きや挨拶は一切不要です。要約内容のみを出力してください。
- 主要ポイント
- 重要な数値や事実
- 結論`
    };

    let prompt = genrePrompts[genre];
    
    // ライティングスタイルを追加
    if (writingStyle) {
      prompt += `\n\n【ライティングスタイル指示】\n${writingStyle}`;
    }
    
    prompt += `\n\n入力内容:\n${text}`;

    if (comment) {
      prompt += `\n\n追加の指示:\n${comment}\n\n重要：上記の指示に従って、内容を省略せず完全に最後まで書いてください。`;
    } else {
      prompt += `\n\n重要：内容を省略せず完全に最後まで書いてください。`;
    }

    return prompt;
  }

  private getMockResponse(text: string, genre: RewriteGenre, comment?: string, customPrompt?: string, writingStyle?: string): GeminiRewriteResponse {
    // カスタムプロンプト、ライティングスタイル、またはジャンルを使用
    void genre;
    void customPrompt;
    void writingStyle;
    let rewrittenText = '';
    
    if (text.includes('プロジェクト') || text.includes('計画')) {
      rewrittenText = `# プロジェクト計画書

## 概要
このプロジェクトは、ユーザーの思考をリアルタイムで整理・構造化するAIツールの開発を目的としています。

## 目標
1. 思考の整理をサポートする直感的なインターフェースの構築
2. リアルタイムでのAIによる文章リライト機能の実装
3. マークダウン形式での出力と各種サービスへの連携

## スケジュール
- フェーズ1: 要件定義と設計（2週間）
- フェーズ2: プロトタイプ開発（3週間）
- フェーズ3: テストと改善（2週間）
- フェーズ4: 本番リリース（1週間）

## 予算
総予算: 500万円
- 人件費: 350万円
- サーバー/インフラ: 80万円
- マーケティング: 50万円
- 予備費: 20万円`;
    } else if (text.includes('会議') || text.includes('ミーティング')) {
      rewrittenText = `# 週次ミーティング議事録

## 日時・場所
- 日時: 2025年6月5日 10:00-11:30
- 場所: 会議室A / Zoom（ハイブリッド開催）

## 参加者
- 山田太郎（プロジェクトマネージャー）
- 鈴木花子（デザイナー）
- 佐藤次郎（エンジニア）
- 高橋恵子（マーケティング）

## 議題
1. 前回のタスク進捗確認
2. 新機能の企画検討
3. リリーススケジュールの調整
4. 次回までのアクションアイテム

## 決定事項
- 新機能は7月リリースに向けて開発を進める
- UIデザインの改善案を来週までに完成させる
- パフォーマンス改善タスクを優先度高で追加

## 次回アクション
- [ ] 山田: リリース計画書の更新
- [ ] 鈴木: 新UIデザインの提案
- [ ] 佐藤: パフォーマンス改善の技術検証
- [ ] 高橋: マーケティング資料の準備`;
    } else if (text.includes('アイデア') || text.includes('企画')) {
      rewrittenText = `# 新機能アイデア集

## 1. リアルタイム共同編集機能
複数ユーザーが同時に同じドキュメントを編集できる機能。Google Docsのような操作感で、各ユーザーの編集位置がカーソルで表示される。

## 2. AIによる要約機能
長文を自動的に要約し、重要ポイントだけを抽出する機能。会議録や長い記事の要点をすばやく把握できる。

## 3. 音声入力からのリアルタイム変換
会議中の発言をリアルタイムで文字起こしし、同時に整形・構造化する機能。議事録作成の手間を大幅に削減できる。

## 4. テンプレートライブラリ
よく使うドキュメント形式（会議録、企画書、報告書など）のテンプレートを用意し、素早くドキュメント作成を開始できる機能。

## 5. データ可視化機能
文章中の数値データを自動検出し、グラフやチャートとして可視化する機能。データの理解と共有を促進する。

## 優先度評価
| 機能 | 実装難易度 | ユーザー価値 | 優先度 |
|------|------------|--------------|--------|
| 共同編集 | 高 | 高 | 中 |
| AI要約 | 中 | 高 | 高 |
| 音声入力 | 高 | 中 | 低 |
| テンプレート | 低 | 中 | 高 |
| データ可視化 | 中 | 中 | 中 |`;
    } else {
      // デフォルトの整形
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length > 0) {
        // 最初の行をタイトルとして扱う
        rewrittenText = `# ${lines[0]}\n\n`;
        
        // 残りの行を内容として整形
        if (lines.length > 1) {
          rewrittenText += `## 概要\n${lines.slice(1).join('\n')}\n\n`;
          
          // コメントがある場合は、それに応じた追加セクションを生成
          if (comment) {
            if (comment.includes('簡潔') || comment.includes('要約')) {
              rewrittenText += `## 要点\n- ${lines.slice(1).join('\n- ')}\n`;
            } else if (comment.includes('箇条書き')) {
              rewrittenText += `## 詳細\n- ${lines.slice(1).join('\n- ')}\n`;
            } else if (comment.includes('専門') || comment.includes('技術')) {
              rewrittenText += `## 技術的詳細\n${lines.slice(1).join('\n')}\n\n### 技術用語\n- 用語1: 説明\n- 用語2: 説明\n`;
            }
          } else {
            // デフォルトの追加セクション
            rewrittenText += `## 詳細\n${lines.slice(1).join('\n')}\n\n## まとめ\n上記の内容をまとめると、重要なポイントは以下の通りです：\n1. ポイント1\n2. ポイント2\n`;
          }
        }
      }
    }
    
    return {
      rewrittenText,
      tokenUsage: {
        input: text.length,
        output: rewrittenText.length,
        total: text.length + rewrittenText.length
      }
    };
  }
}
